import { CloseTwoTone } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

/**
 * Component to show a dialog with autocomplete functionality.
 */
export function AutocompleteDialog<T>({
  title,
  renderTrigger,
  TransitionComponent,
  maxWidth = 'sm',
  fullWidth = true,
  value,
  onChange,
  openInitially,
  ...props
}: AutocompleteProps<T, boolean, boolean, boolean> & {
  title: string;
  TransitionComponent?: DialogProps['TransitionComponent'];
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: DialogProps['fullWidth'];
  renderTrigger: (props: { onClick: () => void; open: boolean }) => JSX.Element;
  openInitially?: boolean;
}) {
  // State to control the visibility of the dialog
  const [open, setOpen] = useState(openInitially || false);
  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // focus on autocomplete when dialog opens
    setTimeout(() => {
      console.log('focusing', ref);
      if (open && ref.current) {
        ref.current?.querySelector('input')?.focus();
      }
    }, 300);
  }, [open]);

  return (
    <>
      {renderTrigger({
        onClick: () => setOpen(true),
        open,
      })}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        TransitionComponent={TransitionComponent}
        fullScreen={isMobile}
      >
        <DialogTitle>
          {title}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseTwoTone />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            minHeight: 580,
          }}
        >
          <Box sx={{ pt: 1 }}>
            <Autocomplete
              {...props}
              ref={ref}
              sx={{ ...props.sx }}
              onChange={(e, v, r) => {
                onChange?.(e, v, r);
                if (r === 'selectOption') setOpen(false);
              }}
              openOnFocus
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
